import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, Snowflake, ArrowUpRight, RefreshCw, Clock, CheckCircle2,
  XCircle, RefreshCcw, Wallet, Zap, AlertCircle, ChevronLeft, ChevronRight
} from 'lucide-react';
import '../styles/App.css';

const THEME = {
  bg: { primary: '#0a0a0f', secondary: '#12121a', tertiary: '#1a1a25', card: 'rgba(26, 26, 37, 0.6)' },
  accent: { primary: '#6366f1', secondary: '#8b5cf6', success: '#10b981', warning: '#f59e0b', danger: '#ef4444', info: '#06b6d4', frost: '#67e8f9' },
  text: { primary: '#f8fafc', secondary: '#94a3b8', muted: '#64748b' },
  border: 'rgba(255, 255, 255, 0.08)',
};

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const STATUS_CONFIG = {
  completed: { color: THEME.accent.success, icon: CheckCircle2, label: 'Completed' },
  pending: { color: THEME.accent.warning, icon: Clock, label: 'Pending' },
  user_deposit_pending: { color: THEME.accent.warning, icon: Clock, label: 'Pending' },
  waiting: { color: THEME.accent.info, icon: Wallet, label: 'Waiting' },
  confirming: { color: THEME.accent.info, icon: RefreshCcw, label: 'Confirming' },
  exchanging: { color: THEME.accent.primary, icon: Zap, label: 'Exchanging' },
  sending: { color: THEME.accent.secondary, icon: ArrowUpRight, label: 'Sending' },
  processing: { color: THEME.accent.primary, icon: Zap, label: 'Processing' },
  failed: { color: THEME.accent.danger, icon: XCircle, label: 'Failed' },
  refunded: { color: THEME.text.muted, icon: RefreshCcw, label: 'Refunded' },
  expired: { color: THEME.text.muted, icon: AlertCircle, label: 'Expired' },
};

const PROVIDER_CONFIG = {
  '1': { name: 'StealthEX', color: '#6366f1' },
  '2': { name: '1Click', color: '#8b5cf6' },
  '3': { name: 'SplitNow', color: '#10b981' },
  '4': { name: 'Exolix', color: '#f59e0b' },
  '5': { name: 'ChangeNOW', color: '#06b6d4' },
  '6': { name: 'SwapKit', color: '#ec4899' },
  '7': { name: 'Baltex', color: '#84cc16' },
  '8': { name: 'BitcoinVN', color: '#f97316' },
};

// Coin icon helper � falls back to a colored letter badge if the image 404s
const CoinIcon = ({ symbol, size = 24 }) => {
  const [errored, setErrored] = useState(false);
  const sym = (symbol || '?').toUpperCase();

  const COLORS = {
    BTC: '#f7931a', ETH: '#627eea', USDT: '#26a17b', USDC: '#2775ca',
    XMR: '#ff6600', SOL: '#14f195', LTC: '#bfbbbb', DOGE: '#c2a633',
    XRP: '#23292f', BNB: '#f3ba2f', ADA: '#0033ad', TRX: '#ef0027',
    DASH: '#008ce7', BCH: '#8dc351', DAI: '#f5ac37', POL: '#8247e5',
    AVAX: '#e84142', LINK: '#2a5ada', UNI: '#ff007a', ATOM: '#2e3148',
  };

  if (errored) {
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: COLORS[sym] || THEME.accent.primary,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.45, fontWeight: '700', color: 'white',
      }}>
        {sym.slice(0, 2)}
      </div>
    );
  }

  return (
    <img
      src={`https://assets.coincap.io/assets/icons/${sym.toLowerCase()}@2x.png`}
      alt={sym}
      width={size}
      height={size}
      onError={() => setErrored(true)}
      style={{ borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }}
    />
  );
};

const timeAgo = (ts) => {
  if (!ts) return 'N/A';
  const then = new Date(ts.replace(' ', 'T') + (ts.includes('Z') ? '' : 'Z')).getTime();
  const diff = Math.floor((Date.now() - then) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const truncateAddr = (a) => (a && a.length > 14 ? `${a.slice(0, 8)}...${a.slice(-6)}` : a || 'N/A');

const ExplorerPage = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total_orders: 0, completed: 0, volume_usd: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const limit = 25;

  const fetchOrders = useCallback(async () => {
    try {
      const [ordersRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/explorer/orders?limit=${limit}&offset=${page * limit}`),
        fetch(`${API_BASE}/explorer/stats`),
      ]);
      if (ordersRes.ok) {
        const data = await ordersRes.json();
        setOrders(data.orders || []);
        setTotal(data.total || 0);
      }
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (err) {
      console.error('Explorer fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 8000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const doLookup = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLookupLoading(true);
    setLookupError('');
    setLookupResult(null);
    try {
      const res = await fetch(`${API_BASE}/explorer/lookup?q=${encodeURIComponent(query.trim())}`);
      if (res.ok) {
        setLookupResult(await res.json());
      } else {
        setLookupError(`No order found for "${query.trim()}"`);
      }
    } catch {
      setLookupError('Lookup failed. Try again.');
    } finally {
      setLookupLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div style={{ minHeight: '100vh', background: THEME.bg.primary, color: THEME.text.primary }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '40px 24px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', color: THEME.text.muted, textDecoration: 'none', gap: '6px', fontSize: '14px' }}>
            <ChevronLeft size={16} /> Home
          </Link>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '8px' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '12px',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 20px rgba(99,102,241,0.3)',
          }}>
            <Snowflake size={26} color="white" />
          </div>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0, letterSpacing: '-0.5px' }}>Swap Explorer</h1>
            <p style={{ color: THEME.text.secondary, margin: 0, fontSize: '14px' }}>Live feed of all Ice Swap orders</p>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', margin: '24px 0 32px' }}>
          {[
            { label: 'Total Orders', value: stats.total_orders.toLocaleString(), color: THEME.accent.primary },
            { label: 'Completed', value: stats.completed.toLocaleString(), color: THEME.accent.success },
            { label: 'Volume', value: `$${stats.volume_usd.toLocaleString()}`, color: THEME.accent.frost },
          ].map((s, i) => (
            <div key={i} style={{ padding: '20px', background: THEME.bg.card, borderRadius: '14px', border: `1px solid ${THEME.border}` }}>
              <div style={{ fontSize: '12px', color: THEME.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>{s.label}</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Lookup */}
        <form onSubmit={doLookup} style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: THEME.text.muted }} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Look up by order ID or deposit address..."
              style={{
                width: '100%', padding: '14px 16px 14px 48px',
                background: THEME.bg.secondary, border: `1px solid ${THEME.border}`,
                borderRadius: '12px', color: THEME.text.primary, fontSize: '14px', outline: 'none',
              }}
            />
          </div>
          <button type="submit" disabled={lookupLoading} style={{
            padding: '14px 24px', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            border: 'none', borderRadius: '12px', color: 'white', fontWeight: '600',
            fontSize: '14px', cursor: lookupLoading ? 'wait' : 'pointer',
          }}>
            {lookupLoading ? 'Searching...' : 'Look Up'}
          </button>
        </form>

        {lookupError && (
          <div style={{ padding: '12px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', color: THEME.accent.danger, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <AlertCircle size={16} /> {lookupError}
          </div>
        )}

        {lookupResult && <OrderCard order={lookupResult} highlight />}

        {/* Feed */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '32px 0 16px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '600', margin: 0, color: THEME.text.secondary }}>Recent Orders</h2>
          <button onClick={fetchOrders} style={{ background: 'none', border: 'none', color: THEME.text.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: THEME.text.muted }}>
            <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : orders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: THEME.text.muted }}>No orders yet</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {orders.map((o) => <OrderCard key={o.id} order={o} />)}
          </div>
        )}

        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '24px' }}>
          <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} style={pageBtnStyle(page === 0)}>
            <ChevronLeft size={16} /> Prev
          </button>
          <span style={{ color: THEME.text.muted, fontSize: '13px' }}>Page {page + 1} / {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={pageBtnStyle(page >= totalPages - 1)}>
            Next <ChevronRight size={16} />
          </button>
        </div>

      </div>
      <style>{`@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const pageBtnStyle = (disabled) => ({
  padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '6px',
  background: THEME.bg.tertiary, border: `1px solid ${THEME.border}`,
  borderRadius: '10px', color: disabled ? THEME.text.muted : THEME.text.primary,
  cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '13px', opacity: disabled ? 0.5 : 1,
});

const OrderCard = ({ order, highlight }) => {
  const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const StatusIcon = status.icon;
  const provider = PROVIDER_CONFIG[order.provider];

  return (
    <div style={{
      padding: '18px 20px', background: THEME.bg.card, borderRadius: '14px',
      border: `1px solid ${highlight ? THEME.accent.primary + '60' : THEME.border}`,
      boxShadow: highlight ? '0 0 25px rgba(99,102,241,0.25)' : 'none',
      display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap',
    }}>
      {/* Pair with icons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '200px' }}>
        <CoinIcon symbol={order.from_asset} />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: '14px', fontWeight: '600' }}>{order.from_amount} {order.from_asset?.toUpperCase()}</span>
          <span style={{ fontSize: '11px', color: THEME.text.muted }}>from</span>
        </div>
        <ArrowUpRight size={16} color={THEME.text.muted} />
        <CoinIcon symbol={order.to_asset} />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: '14px', fontWeight: '600' }}>{order.to_amount} {order.to_asset?.toUpperCase()}</span>
          <span style={{ fontSize: '11px', color: THEME.text.muted }}>to</span>
        </div>
      </div>

      {/* Status */}
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
        background: status.color + '15', borderRadius: '20px', border: `1px solid ${status.color}30`,
        color: status.color, fontSize: '12px', fontWeight: '600',
      }}>
        <StatusIcon size={12} /> {status.label}
      </div>

      {/* Provider */}
      {provider && (
        <span style={{ padding: '4px 10px', background: provider.color + '20', borderRadius: '6px', color: provider.color, fontSize: '12px', fontWeight: '600' }}>
          {provider.name}
        </span>
      )}

      <div style={{ flex: 1 }} />

      {/* Meta */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
        <span style={{ fontSize: '12px', color: THEME.text.muted, display: 'flex', alignItems: 'center', gap: '5px' }}>
          <Clock size={12} /> {timeAgo(order.created_at)}
        </span>
        <span style={{ fontSize: '11px', fontFamily: 'monospace', color: THEME.text.muted }}>
          {order.short_id || truncateAddr(order.id)}
        </span>
        {order.deposit_address && (
          <span style={{ fontSize: '10px', fontFamily: 'monospace', color: THEME.text.muted }}>
            dep: {truncateAddr(order.deposit_address)}
          </span>
        )}
      </div>
    </div>
  );
};

export default ExplorerPage;